<template>
  <div class="schedule-report-page">
    <div class="report-header">
      <div class="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <h3 class="mb-0 fw-bold">Schedule Report</h3>
          <small class="text-muted">#{{ schedule.external_id || 'N/A' }}</small>
        </div>
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-secondary" @click="$router.back()">
            <i class="fas fa-arrow-left"></i> Back
          </button>
          <button type="button" class="btn btn-primary" @click="printReport">
            <i class="fas fa-print"></i> Print / PDF
          </button>
        </div>
      </div>
    </div>

    <div id="report-content" class="report-content">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
        <p class="mt-2">Loading report...</p>
      </div>

      <div v-else>
        <!-- Report Title -->
        <div class="report-title-section">
          <h1 class="report-title">Inspection Report</h1>
          <p class="report-subtitle">{{ workOrder.name || 'Untitled Work Order' }}</p>
          <div class="report-meta">
            <span><strong>Schedule ID:</strong> #{{ schedule.id || 'N/A' }}</span>
            <span><strong>Status:</strong> <span v-html="getStatusBadge(schedule.status)"></span></span>
            <span><strong>Date:</strong> {{ formatDate(schedule.schedule_date) }}</span>
          </div>
        </div>

        <!-- Work Order Details -->
        <div class="report-section">
          <h4 class="section-title">Work Order Details</h4>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Work Order</span>
              <span class="detail-value">{{ workOrder.name || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">External ID</span>
              <span class="detail-value">{{ workOrder.external_id || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Schedule Date</span>
              <span class="detail-value">{{ formatDateTime(schedule.schedule_date) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Status</span>
              <span class="detail-value" v-html="getStatusBadge(schedule.status)"></span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Work Type</span>
              <span class="detail-value">{{ workOrder.work_type || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Priority</span>
              <span class="detail-value">{{ workOrder.priority || 'N/A' }}</span>
            </div>
          </div>
        </div>

        <!-- Questions & Answers -->
        <div class="report-section">
          <h4 class="section-title">Questions & Answers</h4>
          
          <div v-for="(item, index) in template.get_content" :key="item.id" class="question-block">
            <!-- Heading -->
            <div v-if="item.type === 'heading'" class="heading-block">
              <h5 class="heading-title">{{ item.title || 'Heading' }}</h5>
            </div>

            <!-- Section -->
            <div v-else-if="item.type === 'section'" class="section-block">
              <h5 class="section-title">{{ item.title || 'Section' }}</h5>
              <div v-for="(question, qIndex) in item.get_content" :key="question.id" class="question-item">
                <div class="question-header">
                  <span class="question-number">{{ index + 1 }}.{{ qIndex + 1 }}</span>
                  <span class="question-text">{{ question.title || 'Untitled Question' }}</span>
                  <span v-if="question.required" class="required-badge">*</span>
                </div>

                <div class="question-answer">
                  <span class="answer-label">Answer:</span>
                  <span class="answer-value">{{ getAnswerText(question) || 'Not answered' }}</span>
                </div>

                <!-- Logic Triggers -->
                <div v-if="getCorrection(question)" class="logic-trigger correction-trigger">
                  <i class="fas fa-tools"></i> Correction Required
                </div>
                <div v-if="getCorrectiveAction(question)" class="logic-trigger corrective-trigger">
                  <i class="fas fa-exclamation-triangle"></i> Corrective Action Required
                </div>

                <!-- Attachments -->
                <div v-if="getAttachments(question) && getAttachments(question).length > 0" class="question-attachments">
                  <span class="attachment-label">Attachments:</span>
                  <div v-for="(file, idx) in getAttachments(question)" :key="idx" class="attachment-item">
                    <i class="fas fa-paperclip"></i>
                    <a :href="getFilePath(file.file_path)" target="_blank">{{ getFileName(file.file_path) }}</a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Standalone Question -->
            <div v-else-if="item.type === 'question'" class="question-item standalone">
              <div class="question-header">
                <span class="question-number">{{ index + 1 }}</span>
                <span class="question-text">{{ item.title || 'Untitled Question' }}</span>
                <span v-if="item.required" class="required-badge">*</span>
              </div>

              <div class="question-answer">
                <span class="answer-label">Answer:</span>
                <span class="answer-value">{{ getAnswerText(item) || 'Not answered' }}</span>
              </div>

              <div v-if="getCorrection(item)" class="logic-trigger correction-trigger">
                <i class="fas fa-tools"></i> Correction Required
              </div>
              <div v-if="getCorrectiveAction(item)" class="logic-trigger corrective-trigger">
                <i class="fas fa-exclamation-triangle"></i> Corrective Action Required
              </div>

              <div v-if="getAttachments(item) && getAttachments(item).length > 0" class="question-attachments">
                <span class="attachment-label">Attachments:</span>
                <div v-for="(file, idx) in getAttachments(item)" :key="idx" class="attachment-item">
                  <i class="fas fa-paperclip"></i>
                  <a :href="getFilePath(file.file_path)" target="_blank">{{ getFileName(file.file_path) }}</a>
                </div>
              </div>
            </div>
          </div>

          <template v-if="post_inspection_checklist && post_inspection_checklist.id">
            <div class="heading-block">
              <h5 class="heading-title">Post Inspection</h5>
            </div>

            <div v-for="(item, index) in post_inspection_checklist.get_content" :key="item.id" class="question-block">
              <div v-if="item.type === 'heading'" class="heading-block">
                <h5 class="heading-title">{{ item.title || 'Heading' }}</h5>
              </div>

              <div v-else-if="item.type === 'section'" class="section-block">
                <h5 class="section-title">{{ item.title || 'Section' }}</h5>
                <div v-for="(question, qIndex) in item.get_content" :key="question.id" class="question-item">
                  <div class="question-header">
                    <span class="question-number">{{ index + 1 }}.{{ qIndex + 1 }}</span>
                    <span class="question-text">{{ question.title || 'Untitled Question' }}</span>
                    <span v-if="question.required" class="required-badge">*</span>
                  </div>

                  <div class="question-answer">
                    <span class="answer-label">Answer:</span>
                    <span class="answer-value">{{ getAnswerText(question) || 'Not answered' }}</span>
                  </div>

                  <div v-if="getCorrection(question)" class="logic-trigger correction-trigger">
                    <i class="fas fa-tools"></i> Correction Required
                  </div>
                  <div v-if="getCorrectiveAction(question)" class="logic-trigger corrective-trigger">
                    <i class="fas fa-exclamation-triangle"></i> Corrective Action Required
                  </div>

                  <div v-if="getAttachments(question) && getAttachments(question).length > 0" class="question-attachments">
                    <span class="attachment-label">Attachments:</span>
                    <div v-for="(file, idx) in getAttachments(question)" :key="idx" class="attachment-item">
                      <i class="fas fa-paperclip"></i>
                      <a :href="getFilePath(file.file_path)" target="_blank">{{ getFileName(file.file_path) }}</a>
                    </div>
                  </div>
                </div>
              </div>

              <div v-else-if="item.type === 'question'" class="question-item standalone">
                <div class="question-header">
                  <span class="question-number">{{ index + 1 }}</span>
                  <span class="question-text">{{ item.title || 'Untitled Question' }}</span>
                  <span v-if="item.required" class="required-badge">*</span>
                </div>

                <div class="question-answer">
                  <span class="answer-label">Answer:</span>
                  <span class="answer-value">{{ getAnswerText(item) || 'Not answered' }}</span>
                </div>

                <div v-if="getCorrection(item)" class="logic-trigger correction-trigger">
                  <i class="fas fa-tools"></i> Correction Required
                </div>
                <div v-if="getCorrectiveAction(item)" class="logic-trigger corrective-trigger">
                  <i class="fas fa-exclamation-triangle"></i> Corrective Action Required
                </div>

                <div v-if="getAttachments(item) && getAttachments(item).length > 0" class="question-attachments">
                  <span class="attachment-label">Attachments:</span>
                  <div v-for="(file, idx) in getAttachments(item)" :key="idx" class="attachment-item">
                    <i class="fas fa-paperclip"></i>
                    <a :href="getFilePath(file.file_path)" target="_blank">{{ getFileName(file.file_path) }}</a>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="schedule.get_pivot_post_inspection_team?.get_team?.name" class="detail-grid mt-3">
              <div class="detail-item">
                <span class="detail-label">Post Inspection Team</span>
                <span class="detail-value">{{ schedule.get_pivot_post_inspection_team?.get_team?.name || 'N/A' }}</span>
              </div>
            </div>
          </template>
        </div>

        <!-- Corrective Actions Section -->
        <div v-if="schedule.get_corrective_action" class="report-section">
          <h4 class="section-title">Corrective Actions</h4>
          <div class="action-details">
            <div class="detail-item">
              <span class="detail-label">Root Cause / Diagnosis</span>
              <span class="detail-value">{{ schedule.get_corrective_action.root_cause || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Actions Performed</span>
              <span class="detail-value">{{ schedule.get_corrective_action.actions || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Challenges</span>
              <span class="detail-value">{{ schedule.get_corrective_action.challenges || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Action Team</span>
              <span class="detail-value">{{ getTeamNames(schedule.get_action_team) }}</span>
            </div>
          </div>
        </div>

        <!-- Corrections Section -->
        <div v-if="schedule.get_correction" class="report-section">
          <h4 class="section-title">Corrections</h4>
          <div class="action-details">
            <div class="detail-item">
              <span class="detail-label">Corrections</span>
              <span class="detail-value">{{ schedule.get_correction.corrections || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Action Team</span>
              <span class="detail-value">{{ getTeamNames(schedule.get_action_team) }}</span>
            </div>
          </div>
        </div>

        <!-- Verification Section -->
        <div v-if="schedule.get_verification" class="report-section">
          <h4 class="section-title">Verification</h4>
          <div class="verification-details">
            <div class="detail-item">
              <span class="detail-label">Remarks</span>
              <span class="detail-value">{{ schedule.get_verification.verification || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Verification Team</span>
              <span class="detail-value">{{ getVerifierNames() }}</span>
            </div>
          </div>
        </div>

        <!-- Comments Section -->
        <div v-if="comments && comments.length > 0" class="report-section">
          <h4 class="section-title">Comments</h4>
          <div v-for="comment in comments" :key="comment.id" class="comment-item">
            <div class="comment-header">
              <span class="comment-user">{{ comment.get_creator?.first_name || 'Anonymous' }} {{ comment.get_creator?.last_name || '' }}</span>
              <span class="comment-date">{{ formatDate(comment.created_at) }}</span>
            </div>
            <div class="comment-body">
              <div v-if="comment.status !== null && comment.status !== undefined && comment.status !== ''" class="comment-status" v-html="getStatusBadge(comment.status)"></div>
              <p>{{ comment.note }}</p>
              <div v-if="comment.attachment" class="comment-attachment">
                <template v-if="isImageAttachment(comment.attachment)">
                  <a :href="getFilePath(comment.attachment)" target="_blank" class="comment-image-link">
                    <img
                      :src="getFilePath(comment.attachment)"
                      :alt="getFileName(comment.attachment)"
                      class="comment-image-preview"
                    />
                  </a>
                </template>
                <template v-else>
                  <i class="fas fa-paperclip"></i>
                  <a :href="getFilePath(comment.attachment)" target="_blank">{{ getFileName(comment.attachment) }}</a>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Materials Used -->
        <div v-if="schedule.get_materials && schedule.get_materials.length > 0" class="report-section">
          <h4 class="section-title">Materials Used</h4>
          <table class="materials-table">
            <thead>
              <tr>
                <th>Material</th>
                <th>Quantity</th>
                <th>Unit</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="material in schedule.get_materials" :key="material.id">
                <td>{{ material.get_material.name || 'Unknown' }}</td>
                <td>{{ material.quantity || 0 }}</td>
                <td>{{ material.get_material.unit || '' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Footer -->
        <div class="report-footer">
          <p>Report generated on {{ formatDate(new Date()) }}</p>
          <p>This is a system-generated report.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import { getStatusBadge } from '@/utils/getStatusBadge'

export default {
  name: 'ScheduleReport',
  data() {
    return {
      loading: false,
      schedule: {},
      workOrder: {},
      template: { get_content: [] },
      post_inspection_checklist: { get_content: [] },
      comments: [],
      materialsData: [],
      answers: {}
    }
  },
  methods: {
    getStatusBadge,

    async initiate() {
      this.loading = true

      const result = await form_api_submit({
        url: 'wo/schedule/conduct/' + this.$route.params.id,
        method: 'POST',
        show_success_toast: false,
        show_error_toast: true,
        remove_pre_toasts: false
      })

      if (result.success) {
        this.schedule = result.data.schedule || {}
        this.workOrder = result.data.work_order || {}
        this.template = result.data.template || { get_content: [] }
        this.post_inspection_checklist = result.data.post_inspection_checklist || { get_content: [] }
        this.comments = this.schedule.get_comments || []
        this.materialsData = result.data.materials || []
        this.answers = result.data.answers || {}
      }

      this.loading = false
    },

    getAnswerText(question) {
      if (!question) return null

      // Check if answer exists in answers object
      if (this.answers[question.id]) {
        const answer = this.answers[question.id]
        if (answer.answer !== undefined && answer.answer !== null && answer.answer !== '') {
          return answer.answer
        }
      }

      // Check schedule answer
      if (question.get_schedule_answer) {
        const scheduleAnswer = question.get_schedule_answer

        // For multiple choice - get option name from get_option
        if (question.get_response?.type === 'multiple_choice') {
          if (scheduleAnswer.get_answer_options && scheduleAnswer.get_answer_options.length > 0) {
            const options = scheduleAnswer.get_answer_options.map(opt => {
              // Get option name from get_option relationship
              if (opt.get_option) {
                return opt.get_option.name
              }
              // Fallback: try to find option in response options
              const responseOptions = question.get_response?.get_options || []
              const foundOption = responseOptions.find(o => o.id === opt.option_id)
              return foundOption ? foundOption.name : opt.option_id
            })
            return options.join(', ')
          }
          return null
        }

        // For text, number, etc.
        if (scheduleAnswer.answer !== null && scheduleAnswer.answer !== undefined && scheduleAnswer.answer !== '') {
          return scheduleAnswer.answer
        }
      }

      return null
    },

    getCorrection(question) {
      if (this.answers[question.id] && this.answers[question.id].correction !== undefined) {
        return this.answers[question.id].correction
      }
      if (question.get_schedule_answer) {
        return question.get_schedule_answer.correction || false
      }
      return false
    },

    getCorrectiveAction(question) {
      if (this.answers[question.id] && this.answers[question.id].corrective_action !== undefined) {
        return this.answers[question.id].corrective_action
      }
      if (question.get_schedule_answer) {
        return question.get_schedule_answer.corrective_action || false
      }
      return false
    },

    getAttachments(question) {
      return question?.get_attachments || []
    },

    isImageAttachment(path) {
      if (!path) return false
      const extensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg']
      const ext = String(path).toLowerCase().substring(String(path).lastIndexOf('.'))
      return extensions.includes(ext)
    },

    getFileName(path) {
      if (!path) return ''
      return path.split('/').pop()
    },

    getFilePath(path) {
      if (!path) return '#'
      // Remove any duplicate 'dynamic/' prefix
      // const cleanPath = path.replace(/^dynamic\/dynamic\//, 'dynamic/')
      return this.$backend_domain + path
    },

    getTeamNames(team) {
      if (!team || team.length === 0) return 'N/A'
      return team.map(user => (user.first_name || '') + ' ' + (user.last_name || '')).join(', ')
    },

    getVerifierNames() {
      const verifiers = this.workOrder?.get_verifiers || []
      if (!verifiers || verifiers.length === 0) return 'N/A'
      return verifiers.map(user => (user.first_name || '') + ' ' + (user.last_name || '')).join(', ')
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return dateStr
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    formatDateTime(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return dateStr
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    printReport() {
      const printContent = document.getElementById('report-content')
      
      // Create print window
      const printWindow = window.open('', '_blank', 'width=1200,height=800')
      printWindow.document.write(`
        <html>
          <head>
            <title>Schedule Report - ${this.workOrder.name || 'Report'}</title>
            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css">
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                font-family: Arial, sans-serif;
                padding: 40px;
                background: white;
                color: #1a202c;
              }
              .report-content {
                max-width: 1000px;
                margin: 0 auto;
              }
              .report-title-section {
                text-align: center;
                border-bottom: 2px solid #667eea;
                padding-bottom: 20px;
                margin-bottom: 30px;
              }
              .report-title {
                font-size: 28px;
                font-weight: 700;
                color: #1a202c;
                margin-bottom: 4px;
              }
              .report-subtitle {
                font-size: 18px;
                color: #4b5563;
                margin-bottom: 12px;
              }
              .report-meta {
                display: flex;
                justify-content: center;
                gap: 30px;
                font-size: 14px;
                flex-wrap: wrap;
              }
              .report-meta span {
                background: #f8fafc;
                padding: 4px 12px;
                border-radius: 6px;
              }
              .report-section {
                margin-bottom: 30px;
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                padding: 20px;
                page-break-inside: avoid;
              }
              .section-title {
                font-size: 16px;
                font-weight: 600;
                color: #1a202c;
                border-bottom: 2px solid #667eea;
                padding-bottom: 8px;
                margin-bottom: 16px;
              }
              .detail-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 12px;
              }
              .detail-item {
                display: flex;
                flex-direction: column;
                padding: 8px 0;
                border-bottom: 1px solid #f0f0f0;
              }
              .detail-label {
                font-size: 12px;
                font-weight: 600;
                color: #6b7280;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              .detail-value {
                font-size: 14px;
                color: #1a202c;
                margin-top: 2px;
              }
              .heading-block {
                margin: 16px 0 8px 0;
                padding: 8px 12px;
                background: #f8fafc;
                border-radius: 6px;
                border-left: 4px solid #667eea;
              }
              .heading-title {
                font-size: 16px;
                font-weight: 600;
                color: #1a202c;
                margin: 0;
              }
              .section-block {
                margin-bottom: 16px;
              }
              .section-title {
                font-size: 15px;
                font-weight: 600;
                color: #1e293b;
                padding: 8px 12px;
                background: #f1f5f9;
                border-radius: 6px;
                margin-bottom: 12px;
              }
              .question-item {
                padding: 12px 16px;
                border: 1px solid #e9ecef;
                border-radius: 6px;
                margin-bottom: 12px;
                background: #fafbfc;
              }
              .question-item.standalone {
                margin-bottom: 12px;
              }
              .question-header {
                display: flex;
                align-items: baseline;
                gap: 8px;
                margin-bottom: 8px;
              }
              .question-number {
                font-weight: 600;
                color: #667eea;
                font-size: 13px;
                min-width: 30px;
              }
              .question-text {
                font-weight: 500;
                color: #1a202c;
                font-size: 14px;
              }
              .required-badge {
                color: #ef4444;
                font-weight: 700;
                font-size: 16px;
              }
              .question-answer {
                padding: 6px 12px;
                background: #f1f5f9;
                border-radius: 4px;
                margin: 6px 0;
              }
              .answer-label {
                font-weight: 600;
                color: #4b5563;
                font-size: 12px;
                margin-right: 8px;
              }
              .answer-value {
                font-size: 14px;
                color: #1a202c;
              }
              .logic-trigger {
                padding: 4px 12px;
                border-radius: 4px;
                font-size: 12px;
                font-weight: 500;
                margin-top: 6px;
                display: inline-block;
              }
              .correction-trigger {
                background: #fef3c7;
                color: #92400e;
              }
              .corrective-trigger {
                background: #fee2e2;
                color: #991b1b;
              }
              .question-attachments {
                margin-top: 8px;
                display: flex;
                align-items: center;
                gap: 8px;
                flex-wrap: wrap;
              }
              .attachment-label {
                font-size: 12px;
                font-weight: 600;
                color: #6b7280;
              }
              .attachment-item {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                background: #f8f9fa;
                padding: 2px 10px;
                border-radius: 4px;
                font-size: 12px;
                border: 1px solid #e9ecef;
              }
              .attachment-item a {
                color: #667eea;
                text-decoration: none;
              }
              .attachment-item a:hover {
                text-decoration: underline;
              }
              .action-details, .verification-details {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 12px;
              }
              .comment-item {
                padding: 12px 0;
                border-bottom: 1px solid #f0f0f0;
              }
              .comment-item:last-child {
                border-bottom: none;
              }
              .comment-header {
                display: flex;
                justify-content: space-between;
                margin-bottom: 4px;
              }
              .comment-user {
                font-weight: 600;
                color: #1a202c;
                font-size: 13px;
              }
              .comment-date {
                font-size: 12px;
                color: #6b7280;
              }
              .comment-body p {
                font-size: 14px;
                color: #334155;
                margin: 0 0 4px 0;
              }
              .comment-status {
                font-size: 11px;
                font-weight: 600;
                padding: 2px 10px;
                border-radius: 4px;
                display: inline-block;
                margin-bottom: 4px;
              }
              .status-hold {
                background: #fee2e2;
                color: #991b1b;
              }
              .status-release {
                background: #d1fae5;
                color: #065f46;
              }
              .comment-attachment {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                color: #667eea;
              }
              .comment-attachment a {
                color: #667eea;
                text-decoration: none;
              }
              .comment-attachment a:hover {
                text-decoration: underline;
              }
              .comment-image-link {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                max-width: 100%;
              }
              .comment-image-preview {
                display: block;
                max-width: 100px;
                width: auto;
                height: auto;
                max-height: 100px;
                object-fit: contain;
                border-radius: 6px;
                border: 1px solid #e5e7eb;
                background: #fff;
              }
              .materials-table {
                width: 100%;
                border-collapse: collapse;
                font-size: 14px;
              }
              .materials-table th {
                text-align: left;
                padding: 8px 12px;
                background: #f8fafc;
                border-bottom: 2px solid #e9ecef;
                font-weight: 600;
                color: #4b5563;
              }
              .materials-table td {
                padding: 8px 12px;
                border-bottom: 1px solid #f0f0f0;
              }
              .report-footer {
                text-align: center;
                padding-top: 20px;
                border-top: 1px solid #e5e7eb;
                font-size: 12px;
                color: #6b7280;
                margin-top: 20px;
              }
              .badge-status {
                display: inline-flex;
                align-items: center;
                gap: 4px;
                padding: 2px 12px;
                border-radius: 12px;
                font-size: 11px;
                font-weight: 600;
              }
              @media print {
                body { padding: 20px; }
                .report-section { page-break-inside: avoid; }
              }
              @media (max-width: 768px) {
                .detail-grid, .action-details, .verification-details {
                  grid-template-columns: 1fr;
                }
                .report-meta {
                  flex-direction: column;
                  gap: 8px;
                  align-items: center;
                }
              }
            </style>
          </head>
          <body>
            ${printContent.innerHTML}
            <script>
              window.onload = function() {
                window.print()
                window.onafterprint = function() {
                  window.close()
                }
              }
            <\/script>
          </body>
        </html>
      `)
      printWindow.document.close()
    }
  },

  mounted() {
    this.initiate()
  }
}
</script>

<style scoped>
.schedule-report-page {
  min-height: 100vh;
  background: #f6f7fb;
  padding: 20px;
}

.report-header {
  background: white;
  padding: 20px 24px;
  border-radius: 14px;
  margin-bottom: 20px;
  border: 1px solid #ececf3;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
  max-width: 1000px;
  margin-left: auto;
  margin-right: auto;
}

.report-header h3 {
  color: #1a202c;
}

.report-content {
  max-width: 1000px;
  margin: 0 auto;
  background: white;
  border: 1px solid #ececf3;
  border-radius: 14px;
  padding: 40px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
}

/* Report Title Section */
.report-title-section {
  text-align: center;
  border-bottom: 2px solid #667eea;
  padding-bottom: 20px;
  margin-bottom: 30px;
}

.report-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 4px;
}

.report-subtitle {
  font-size: 18px;
  color: #4b5563;
  margin-bottom: 12px;
}

.report-meta {
  display: flex;
  justify-content: center;
  gap: 30px;
  font-size: 14px;
  flex-wrap: wrap;
}

.report-meta span {
  background: #f8fafc;
  padding: 4px 14px;
  border-radius: 6px;
}

/* Report Sections */
.report-section {
  margin-bottom: 30px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  border-bottom: 2px solid #667eea;
  padding-bottom: 8px;
  margin-bottom: 16px;
}

/* Detail Grid */
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  color: #1a202c;
  margin-top: 2px;
}

/* Heading Block */
.heading-block {
  margin: 16px 0 8px 0;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 6px;
  border-left: 4px solid #667eea;
}

.heading-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

/* Section Block */
.section-block {
  margin-bottom: 16px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  padding: 8px 12px;
  background: #f1f5f9;
  border-radius: 6px;
  margin-bottom: 12px;
}

/* Question Item */
.question-item {
  padding: 12px 16px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  margin-bottom: 12px;
  background: #fafbfc;
}

.question-item.standalone {
  margin-bottom: 12px;
}

.question-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
}

.question-number {
  font-weight: 600;
  color: #667eea;
  font-size: 13px;
  min-width: 30px;
}

.question-text {
  font-weight: 500;
  color: #1a202c;
  font-size: 14px;
}

.required-badge {
  color: #ef4444;
  font-weight: 700;
  font-size: 16px;
}

/* Question Answer */
.question-answer {
  padding: 6px 12px;
  background: #f1f5f9;
  border-radius: 4px;
  margin: 6px 0;
}

.answer-label {
  font-weight: 600;
  color: #4b5563;
  font-size: 12px;
  margin-right: 8px;
}

.answer-value {
  font-size: 14px;
  color: #1a202c;
}

/* Logic Triggers */
.logic-trigger {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  margin-top: 6px;
  display: inline-block;
}

.correction-trigger {
  background: #fef3c7;
  color: #92400e;
}

.corrective-trigger {
  background: #fee2e2;
  color: #991b1b;
}

/* Attachments */
.question-attachments {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.attachment-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.attachment-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #f8f9fa;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;
  border: 1px solid #e9ecef;
}

.attachment-item a {
  color: #667eea;
  text-decoration: none;
}

.attachment-item a:hover {
  text-decoration: underline;
}

/* Action & Verification */
.action-details,
.verification-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

/* Comments */
.comment-image-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
}

.comment-image-preview {
  display: block;
  max-width: 100px;
  width: auto;
  height: auto;
  max-height: 100px;
  object-fit: contain;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #fff;
}

.comment-item {
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.comment-item:last-child {
  border-bottom: none;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.comment-user {
  font-weight: 600;
  color: #1a202c;
  font-size: 13px;
}

.comment-date {
  font-size: 12px;
  color: #6b7280;
}

.comment-body p {
  font-size: 14px;
  color: #334155;
  margin: 0 0 4px 0;
}

.comment-status {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 4px;
}

.status-hold {
  background: #fee2e2;
  color: #991b1b;
}

.status-release {
  background: #d1fae5;
  color: #065f46;
}

.comment-attachment {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #667eea;
}

.comment-attachment a {
  color: #667eea;
  text-decoration: none;
}

.comment-attachment a:hover {
  text-decoration: underline;
}

/* Materials Table */
.materials-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.materials-table th {
  text-align: left;
  padding: 8px 12px;
  background: #f8fafc;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  color: #4b5563;
}

.materials-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
}

/* Footer */
.report-footer {
  text-align: center;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
  color: #6b7280;
  margin-top: 20px;
}

/* Responsive */
@media (max-width: 768px) {
  .schedule-report-page {
    padding: 12px;
  }

  .report-content {
    padding: 20px;
  }

  .detail-grid,
  .action-details,
  .verification-details {
    grid-template-columns: 1fr;
  }

  .report-meta {
    flex-direction: column;
    gap: 8px;
    align-items: center;
  }

  .report-title {
    font-size: 22px;
  }

  .report-subtitle {
    font-size: 16px;
  }
}
</style>